import type { ReactNode } from 'react';

import { useT } from '@/i18n/LocaleProvider';

export type MarkdownPreviewProps = {
  markdown: string;
};

type PreviewBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string; key: string }
  | { kind: 'image'; alt: string; src: string; key: string }
  | { kind: 'list'; items: string[]; key: string }
  | { kind: 'paragraph'; text: string; key: string };

type PreviewFact = {
  key: string;
  value: string;
};

type PreviewDocument = {
  facts: PreviewFact[];
  blocks: PreviewBlock[];
};

const factLinePattern = /^####\s*-\s*([^:]+):\s*(.+)$/;
const imageLinePattern = /^!\[([^\]]*)\]\(([^)]+)\)$/;
const inlineTokenPattern = /(!\[[^\]]*\]\([^)]+\)|\[\[[^\]]+\]\]|`[^`]+`|\*\*[^*]+\*\*)/g;

export function MarkdownPreview({ markdown }: MarkdownPreviewProps) {
  const t = useT();
  const preview = parsePreviewDocument(markdown);

  if (preview.facts.length === 0 && preview.blocks.length === 0) {
    return <div className="markdown-preview-empty">{t('preview_empty')}</div>;
  }

  return (
    <div className="markdown-preview note-body" data-testid="markdown-preview">
      {preview.facts.length > 0 ? (
        <dl className="fact-grid">
          {preview.facts.map((fact) => (
            <div className="fact-row" key={fact.key}>
              <dt>{fact.key}</dt>
              <dd>{renderInline(fact.value)}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {preview.blocks.map((block) => {
        switch (block.kind) {
          case 'heading': {
            const Heading = `h${block.level}` as const;

            return <Heading key={block.key}>{renderInline(block.text)}</Heading>;
          }
          case 'image':
            return <img alt={block.alt} key={block.key} src={block.src} />;
          case 'list':
            return (
              <ul key={block.key}>
                {block.items.map((item) => (
                  <li key={`${block.key}-${itemKey(item)}`}>{renderInline(item)}</li>
                ))}
              </ul>
            );
          case 'paragraph':
            return <p key={block.key}>{renderInline(block.text)}</p>;
        }

        return null;
      })}
    </div>
  );
}

function parsePreviewDocument(markdown: string): PreviewDocument {
  const facts: PreviewFact[] = [];
  const blocks: PreviewBlock[] = [];
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = (index: number) => {
    if (paragraph.length === 0) {
      return;
    }

    blocks.push({
      kind: 'paragraph',
      text: paragraph.join(' '),
      key: `p-${index}-${blocks.length}`,
    });
    paragraph = [];
  };

  const flushList = (index: number) => {
    if (list.length === 0) {
      return;
    }

    blocks.push({
      kind: 'list',
      items: list,
      key: `ul-${index}-${blocks.length}`,
    });
    list = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph(index);
      flushList(index);
      return;
    }

    const factMatch = trimmed.match(factLinePattern);
    if (factMatch?.[1] && factMatch[2]) {
      flushParagraph(index);
      flushList(index);
      facts.push({ key: factMatch[1].trim(), value: factMatch[2].trim() });
      return;
    }

    const imageMatch = trimmed.match(imageLinePattern);
    if (imageMatch?.[2]) {
      flushParagraph(index);
      flushList(index);
      blocks.push({
        kind: 'image',
        alt: imageMatch[1] ?? '',
        src: imageMatch[2],
        key: `img-${index}-${blocks.length}`,
      });
      return;
    }

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch?.[1] && headingMatch[2]) {
      flushParagraph(index);
      flushList(index);
      blocks.push({
        kind: 'heading',
        level: headingMatch[1].length as 1 | 2 | 3,
        text: headingMatch[2],
        key: `h-${index}-${blocks.length}`,
      });
      return;
    }

    const listMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (listMatch?.[1]) {
      flushParagraph(index);
      list.push(listMatch[1]);
      return;
    }

    flushList(index);
    paragraph.push(trimmed);
  });

  flushParagraph(lines.length);
  flushList(lines.length);

  return { facts, blocks };
}

function renderInline(value: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;

  for (const match of value.matchAll(inlineTokenPattern)) {
    const token = match[0] ?? '';
    const index = match.index ?? 0;

    if (index > cursor) {
      nodes.push(value.slice(cursor, index));
    }

    nodes.push(renderToken(token, index));
    cursor = index + token.length;
  }

  if (cursor < value.length) {
    nodes.push(value.slice(cursor));
  }

  return nodes.length > 0 ? nodes : [value];
}

function renderToken(token: string, key: number): ReactNode {
  const imageMatch = token.match(imageLinePattern);
  if (imageMatch?.[2]) {
    return <img alt={imageMatch[1] ?? ''} key={key} src={imageMatch[2]} />;
  }

  if (token.startsWith('[[') && token.endsWith(']]')) {
    const inner = token.slice(2, -2);
    const [target, label] = inner.split('|');

    return (
      <span className="wiki-link" key={key}>
        {(label || target || inner).trim()}
      </span>
    );
  }

  if (token.startsWith('`') && token.endsWith('`')) {
    return <code key={key}>{token.slice(1, -1)}</code>;
  }

  if (token.startsWith('**') && token.endsWith('**')) {
    return <strong key={key}>{token.slice(2, -2)}</strong>;
  }

  return token;
}

function itemKey(item: string): string {
  let hash = 0;

  for (let index = 0; index < item.length; index += 1) {
    hash = (hash * 31 + item.charCodeAt(index)) % 100000;
  }

  return `${hash}-${item.length}`;
}
